import { useEffect, useState, useCallback, useMemo } from 'react';
import Button from '../../../components/Button';
import { useAuth } from '../../../hooks/useAuth';
import { EditQuestTemplate } from './EditQuestTemplate';
import { JourneyDaySection } from './JourneyDaySection';
import { QuestJourneyManager } from './QuestJourneyManager';
import { QuestTemplatesStatistics } from './QuestTemplatesStatistics';
import { FloatingSearchBar } from './FloatingSearchBar';
import { QuestTemplateSearchResults } from './QuestTemplateSearchResults';
import { questsService } from '../services/quests-service';
import type { QuestJourney, QuestTemplateFirestore, QuestType, QuestTemplateChatFirestore, QuestTemplateCheckInFirestore } from '../services/questTypes';

// Helper function to aggregate quest types by journey day
const getQuestTypesByDay = (allQuestTemplates: QuestTemplateFirestore[], journeyDayId: string): Record<QuestType, { published: number; unpublished: number }> => {
    const questTypeCounts: Record<QuestType, { published: number; unpublished: number }> = {
        'chat': { published: 0, unpublished: 0 },
        'check-in': { published: 0, unpublished: 0 },
        'story': { published: 0, unpublished: 0 },
        'lesson': { published: 0, unpublished: 0 },
        'community-question': { published: 0, unpublished: 0 },
        'tool': { published: 0, unpublished: 0 },
        'feedback': { published: 0, unpublished: 0 },
        'rate': { published: 0, unpublished: 0 },
    };

    // Filter templates for this specific journey day and count by type and publish status
    allQuestTemplates
        .filter(template => template.journeyDayId === journeyDayId)
        .forEach(template => {
            if (template.isPublished) {
                questTypeCounts[template.type].published += 1;
            } else {
                questTypeCounts[template.type].unpublished += 1;
            }
        });

    return questTypeCounts;
};

// Helper function to determine if a journey day shows quest insights
const isInsightsDay = (journeyDayId: string): boolean => {
    const dayNumber = parseInt(journeyDayId.replace('day_', ''));
    // Insights shown on days 6, 12, 18, 24... (multiples of 6)
    return dayNumber > 0 && dayNumber % 6 === 0;
};

// Interface for search results
interface SearchResult {
    questTemplate: QuestTemplateFirestore;
    journeyDayId: string;
    matchedText: string;
}

export const QuestTemplates = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [editingQuest, setEditingQuest] = useState<QuestTemplateFirestore | null>(null);
    const [openJourneyDayId, setOpenJourneyDayId] = useState<string | null>(null);
    const [questJourneyDays, setQuestJourneyDays] = useState<string[]>([]);
    const [questJourney, setQuestJourney] = useState<QuestJourney | null>(null);
    const [allQuestTemplates, setAllQuestTemplates] = useState<QuestTemplateFirestore[]>([]);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [daysRefreshTrigger, setDaysRefreshTrigger] = useState(0);
    const [journeyRefreshTrigger, setJourneyRefreshTrigger] = useState(0);
    const [newQuestOrder, setNewQuestOrder] = useState<number>(0);
    const [newQuestType, setNewQuestType] = useState<QuestType | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [showSearchResults, setShowSearchResults] = useState(false);
    const { user } = useAuth();

    useEffect(() => {
        setIsLoading(true);
        const fetchQuestJourneyDays = async () => {
            const questJourneyDaysRequest = questsService.getQuestJourneyDays();
            const questJourneyRequest = questsService.getCurrentUserJourney(user!.uid);
            const allQuestTemplatesRequest = questsService.getAllQuestTemplates();
            const [questJourneyDays, questJourney, allQuestTemplates] = await Promise.all([
                questJourneyDaysRequest,
                questJourneyRequest,
                allQuestTemplatesRequest
            ]);
            console.log('questJourney', questJourney);
            setQuestJourneyDays(questJourneyDays);
            setQuestJourney(questJourney);
            setAllQuestTemplates(allQuestTemplates);
            setIsLoading(false);
        };
        fetchQuestJourneyDays();
    }, [daysRefreshTrigger, journeyRefreshTrigger, refreshTrigger, user]);

    const handleEdit = (questTemplate: QuestTemplateFirestore) => {
        setEditingQuest(questTemplate);
    };

    const handleCreateNew = (order: number, questType: QuestType) => {
        setNewQuestOrder(order);
        setNewQuestType(questType);
        setEditingQuest({} as QuestTemplateFirestore); // Empty object to indicate new quest
    };

    const handleToggle = (journeyDayId: string) => {
        if (openJourneyDayId === journeyDayId) {
            setOpenJourneyDayId(null);
        } else {
            setOpenJourneyDayId(journeyDayId);
        }
    };

    const handleSave = async (questTemplate: QuestTemplateFirestore) => {
        if (!openJourneyDayId) {
            return;
        }

        if (editingQuest?.id) {
            // Update existing quest template
            await questsService.updateQuestTemplate(openJourneyDayId, questTemplate);
        } else {
            // Create new quest template
            await questsService.createQuestTemplate(openJourneyDayId, questTemplate);
        }

        setEditingQuest(null);
        setRefreshTrigger(prev => prev + 1); // Trigger refresh of quest lists
    };

    const handleSaveAndContinue = async (questTemplate: QuestTemplateFirestore) => {
        if (!openJourneyDayId) {
            return;
        }

        if (editingQuest?.id) {
            // Update existing quest template
            await questsService.updateQuestTemplate(openJourneyDayId, questTemplate);
        } else {
            // Create new quest template and get the created quest with real database ID
            const createdQuest = await questsService.createQuestTemplate(openJourneyDayId, questTemplate);
            // Update the editing quest to the newly created one with real ID
            setEditingQuest(createdQuest);
        }

        setRefreshTrigger(prev => prev + 1); // Trigger refresh of quest lists
        // Note: We don't close the modal (setEditingQuest(null)) to allow continued editing
    };

    const handleDelete = async (questTemplate: QuestTemplateFirestore, journeyDayId: string): Promise<boolean> => {
        // confirm delete
        const confirmed = window.confirm('Are you sure you want to delete this quest template?');
        if (!confirmed) {
            return false;
        }
        await questsService.deleteQuestTemplate(journeyDayId, questTemplate);
        return true;
    };

    const handleCancel = () => {
        setEditingQuest(null);
        setNewQuestType(null);
    };

    const handleMoveToDay = async (questTemplate: QuestTemplateFirestore, sourceJourneyDayId: string, targetJourneyDayId: string) => {
        const sourceDayNumber = sourceJourneyDayId.replace('day_', '');
        const targetDayNumber = targetJourneyDayId.replace('day_', '');

        const confirmed = window.confirm(
            `Are you sure you want to move this quest from Day ${sourceDayNumber} to Day ${targetDayNumber}? It will be unpublished in the new day.`
        );

        if (!confirmed) {
            return;
        }

        await questsService.moveQuestTemplate(
            sourceJourneyDayId,
            targetJourneyDayId,
            questTemplate
        );

        // Trigger refresh to update both source and target days
        setRefreshTrigger(prev => prev + 1);
    };

    const handleCreateJourneyDay = async () => {
        const confirmed = window.confirm('Are you sure you want to create a new journey day?');
        if (!confirmed) {
            return;
        }
        await questsService.createJourneyDay(questJourneyDays.length);
        setDaysRefreshTrigger(prev => prev + 1);
    };

    const handleJourneyUpdate = () => {
        setJourneyRefreshTrigger(prev => prev + 1);
    };

    // Search functionality
    const handleSearch = useCallback((query: string) => {
        setSearchQuery(query);
        setShowSearchResults(query.trim().length > 0);
    }, []);

    const searchResults = useMemo<SearchResult[]>(() => {
        if (!searchQuery.trim()) {
            return [];
        }

        const query = searchQuery.toLowerCase();
        const results: SearchResult[] = [];

        for (const template of allQuestTemplates) {
            let matchedText = '';

            // Search in textTemplatingPrompt (available for all quest types)
            if (template.textTemplatingPrompt?.toLowerCase().includes(query)) {
                matchedText = template.textTemplatingPrompt;
            }

            // Search in aiPromptGuidance (available for chat and check-in types)
            if (!matchedText && (template.type === 'chat' || template.type === 'check-in')) {
                const chatOrCheckInTemplate = template as QuestTemplateChatFirestore | QuestTemplateCheckInFirestore;
                if (chatOrCheckInTemplate.aiPromptGuidance?.toLowerCase().includes(query)) {
                    matchedText = chatOrCheckInTemplate.aiPromptGuidance;
                }
            }

            // If we found a match, add to results
            if (matchedText && template.journeyDayId) {
                results.push({
                    questTemplate: template,
                    journeyDayId: template.journeyDayId,
                    matchedText
                });
            }

            // Limit to 5 results
            if (results.length >= 5) {
                break;
            }
        }

        return results;
    }, [searchQuery, allQuestTemplates]);

    const handleSelectSearchResult = useCallback((journeyDayId: string) => {
        setOpenJourneyDayId(journeyDayId);
        setShowSearchResults(false);
        setSearchQuery('');

        // Scroll to the journey day section after a brief delay to allow rendering
        setTimeout(() => {
            const element = document.getElementById(`journey-day-${journeyDayId}`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 100);
    }, []);

    const handleCloseSearchResults = useCallback(() => {
        setShowSearchResults(false);
    }, []);

    // Handle Escape key to close search results
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && showSearchResults) {
                setShowSearchResults(false);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [showSearchResults]);

    if (isLoading) {
        return <div>Loading...</div>;
    }

    return (
        <>
            <QuestJourneyManager questJourney={questJourney} onJourneyUpdate={handleJourneyUpdate} journeyDayTotalCount={questJourneyDays.length} />

            {/* Statistics Section */}
            <QuestTemplatesStatistics allQuestTemplates={allQuestTemplates} />

            <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
                <h2 className="text-xl font-semibold text-gray-700">Quest Templates</h2>
                <Button onClick={handleCreateJourneyDay} variant="primary">Create Journey Day</Button>
            </div>
            <div className="flex flex-col gap-4 pb-16">
                {questJourneyDays.map((journeyDayId) => {
                    const questTypeCounts = getQuestTypesByDay(allQuestTemplates, journeyDayId);
                    return (
                        <div key={journeyDayId} id={`journey-day-${journeyDayId}`}>
                            <JourneyDaySection
                                journeyDayId={journeyDayId}
                                isOpen={openJourneyDayId === journeyDayId}
                                isCurrentDayInJourney={`day_${questJourney?.currentDay}` === journeyDayId}
                                isInsightsDay={isInsightsDay(journeyDayId)}
                                availableJourneyDays={questJourneyDays}
                                questTypeCounts={questTypeCounts}
                                onEdit={handleEdit}
                                onDelete={(questTemplate) => handleDelete(questTemplate, journeyDayId)}
                                onCreateNew={(maxOrder, questType) => {
                                    setOpenJourneyDayId(journeyDayId);
                                    handleCreateNew(maxOrder, questType);
                                }}
                                onMoveToDay={handleMoveToDay}
                                onToggle={() => handleToggle(journeyDayId)}
                                refreshTrigger={refreshTrigger}
                            />
                        </div>
                    );
                })}
            </div>

            {/* Edit Modal */}
            {editingQuest && (
                <EditQuestTemplate
                    questTemplate={editingQuest}
                    onSave={handleSave}
                    onSaveAndContinue={handleSaveAndContinue}
                    onCancel={handleCancel}
                    journeyDay={openJourneyDayId || ''}
                    initialOrder={editingQuest.id ? undefined : newQuestOrder}
                    questType={editingQuest.id ? undefined : newQuestType || undefined}
                    allQuestTemplates={allQuestTemplates}
                />
            )}

            {/* Floating Search Bar */}
            <FloatingSearchBar
                onSearch={handleSearch}
                resultsCount={searchResults.length}
                isSearching={searchQuery.trim().length > 0}
            />

            {/* Search Results Modal */}
            {showSearchResults && (
                <QuestTemplateSearchResults
                    results={searchResults}
                    searchQuery={searchQuery}
                    onSelect={handleSelectSearchResult}
                    onClose={handleCloseSearchResults}
                />
            )}
        </>
    );
};

